import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconButton, Avatar } from "@mui/material";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import "../../css/Navbar.css";
import { logout, getUsername, updateLanguage } from '../../services/users/AuthService';
import { useTheme } from "../../context/ThemeContext";
import { getBreadcrumbTrail } from "../../config/menuItems";

const getInitials = (name) => {
  if (!name) return "?";
  return name
    .split(/[\s._-]+/)
    .map((w) => w[0]?.toUpperCase())
    .slice(0, 2)
    .join("");
};

export default function Navbar() {

  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [username, setUsername] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);
  const { mode, toggleTheme } = useTheme();

  useEffect(() => {
    setUsername(getUsername());
  }, []);

  const breadcrumbTrail = useMemo(() => {
    if (location.pathname === "/homepage") return ["sidebar.home"];
    return getBreadcrumbTrail(location.pathname) || [];
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const otherLanguage = i18n.language === "en" ? "PT" : "EN";
  const handleLanguageChange = () => {
    updateLanguage(otherLanguage);
  };

  const handleClickOutside = (event) => {
    if (menuRef.current && !menuRef.current.contains(event.target)) {
      setMenuOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const initials = getInitials(username);

  return (
    <header className="navbar">
      <div className="navbar-left">
        {breadcrumbTrail.length > 0 && (
          <nav className="breadcrumb" aria-label={t("navbar.breadcrumbNav")}>
            {breadcrumbTrail.map((label, index) => (
              <span className="breadcrumb-item" key={`${label}-${index}`}>
                {index > 0 && (
                  <ChevronRightIcon className="breadcrumb-separator" fontSize="inherit" />
                )}
                <span
                  className={
                    index === breadcrumbTrail.length - 1
                      ? "breadcrumb-current"
                      : "breadcrumb-link"
                  }
                >
                  {t(label)}
                </span>
              </span>
            ))}
          </nav>
        )}
      </div>

      <div className="navbar-right" ref={menuRef}>
        <IconButton
          onClick={handleLanguageChange}
          title={t("navbar.switchLanguage")}
          sx={{ color: "var(--text-muted)", mr: 0.5, fontSize: "0.7rem", fontWeight: 700 }}
          size="small"
        >
          {otherLanguage}
        </IconButton>

        <IconButton
          onClick={toggleTheme}
          title={mode === "dark" ? t("navbar.lightMode") : t("navbar.darkMode")}
          sx={{ color: "var(--text-muted)", mr: 0.5 }}
          size="small"
        >
          {mode === "dark" ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
        </IconButton>

        <div className="user-info">
          <span className="username-text">{username || t("navbar.user")}</span>
        </div>

        <div
          className="user-avatar-wrapper"
          onClick={() => setMenuOpen((prev) => !prev)}
          title={t("navbar.userOptions")}
        >
          <Avatar
            sx={{
              width: 34,
              height: 34,
              bgcolor: "var(--color-primary)",
              color: "#fff",
              fontSize: "0.85rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {initials}
          </Avatar>
        </div>

        {menuOpen && (
          <div className="dropdown-menu">
            <div className="dropdown-header">
              <Avatar sx={{ width: 40, height: 40, bgcolor: "var(--color-primary)", color: "#fff", fontSize: "1rem", fontWeight: 600 }}>
                {initials}
              </Avatar>
              <div className="dropdown-user-info">
                <span className="dropdown-username">{username || t("navbar.user")}</span>
                <span className="dropdown-role">{t("navbar.administrator")}</span>
              </div>
            </div>
            <div className="dropdown-divider" />
            <button onClick={() => { setMenuOpen(false); navigate("/perfil"); }}>{t("navbar.profile")}</button>
            <button onClick={() => { setMenuOpen(false); navigate("/users"); }}>{t("navbar.userManagement")}</button>
            <div className="dropdown-divider" />
            <button onClick={handleLogout} className="dropdown-logout">{t("navbar.logout")}</button>
          </div>
        )}
      </div>
    </header>
  );
}
